import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useToast } from 'primevue/usetoast';
import { z } from 'zod';
import { useAuthSession } from '~/composables/auth/useAuthSession';
import type { LoginForm } from '~/types/auth/forms/LoginForm';
import LoginFormContent from '~/utils/contents/auth/forms/LoginForm';
import { $parseErrorMessage } from '~/utils/helpers';

export default function useLogin({
  initialForm,
}: { initialForm?: Partial<LoginForm> } = {}) {
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});
  const form = ref<LoginForm>({
    ...getInitialState(),
    ...Object.fromEntries(
      Object.entries(initialForm || {}).filter(([, v]) => v !== undefined),
    ),
  });

  const { signIn } = useAuthSession();
  const toast = useToast();

  const validations = z.object({
    email: z
      .string()
      .min(1, {
        message: `${LoginFormContent.fields.email.label} is required.`,
      })
      .email({
        message: `${LoginFormContent.fields.email.label} must be a valid email.`,
      }),
    password: z.string().min(1, {
      message: `${LoginFormContent.fields.password.label} is required.`,
    }),
  });
  const resolver = zodResolver(validations);

  const login = async (): Promise<boolean> => {
    if (!validations.safeParse(form.value).success) {
      return false;
    }

    loading.value = true;

    try {
      const { error } = await signIn({
        username: form.value.email,
        password: form.value.password,
        inviteCode: form.value.invite_code,
      });

      if (error) {
        errors.value = error.data?.errors || {};
        if (
          error.data?.success === false &&
          errors.value &&
          Object.keys(errors.value).length === 0
        ) {
          errors.value = { password: [error.data.message] };
        }
        return false;
      } else {
        reset();
        return true;
      }
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: $parseErrorMessage(error),
        life: 3000,
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const reset = (): void => {
    form.value = getInitialState();
  };

  function getInitialState(): LoginForm {
    return {
      email: '',
      password: '',
    };
  }

  return {
    loading,
    form,
    errors,
    validations,
    resolver,

    login,
    reset,
  };
}
